// Floor sale → till, through the server's estimates (s_estimate, status PE).
// The phone saves its bag as an estimate marked floorSale "Y"; any till in
// the store lists today's floor estimates and opens one onto its own cart.
// Loading mirrors the till's estimate scan (Core/scanner.js): the bill keeps
// the estimateId (the server marks the estimate done when the bill syncs) and
// gets a new sOrderID and this till's next document number.
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";

export const FLOOR_SALE = "Y";
const newId = () => uuidv4().replace(/-/g, "").toUpperCase();

export function floorEstimatePayload(cart, tillData, now = dayjs()) {
  const estimateId = cart?.estimateId || newId();
  return {
    bunitId: tillData?.tillAccess?.csBunit?.csBunitId,
    estimateId,
    date: dayjs(now).format("YYYY-MM-DD HH:mm:ss"),
    status: "PE",
    isAdvanceOrder: "N",
    estimateJson: { ...cart, estimateId, floorSale: FLOOR_SALE, floorSentAt: dayjs(now).format("YYYY-MM-DD HH:mm:ss") },
  };
}

// → { ok: true, estimateId, documentno } or { ok: false, message }. Never throws.
export async function sendFloorBill(cart, tillData, upsertEstimate, now) {
  // A floor bill is items only: payments and returns are taken at the till.
  if ((cart?.payments || []).length > 0 || cart?.isReturn === true) return { ok: false, message: "This bill already has a payment. Finish it at the till." };
  const payload = floorEstimatePayload(cart, tillData, now);
  if (!payload.bunitId) return { ok: false, message: "This phone isn't linked to a store. Send the bill from the till." };
  let res;
  try {
    res = await upsertEstimate(payload);
  } catch {
    res = null;
  }
  const out = res?.data?.data?.upsertEstimate;
  if (String(out?.status) === "200") {
    const documentno = String(out.message || "").split(": ")[1] || "";
    return { ok: true, estimateId: payload.estimateId, documentno: documentno.trim() };
  }
  return { ok: false, message: "The bill couldn't be sent to the till. It is still in the bag; try again." };
}

function parseJson(value, fallback) {
  if (value && typeof value === "object") return value;
  try {
    return JSON.parse(value || "");
  } catch {
    return fallback;
  }
}

// Today's floor bills waiting at this store, newest first.
// → { ok: true, bills: [{ estimateId, documentno, created, cart }] } or { ok: false }.
export async function listFloorBills(tillData, getEstimates, date = dayjs()) {
  const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
  if (!bunitId) return { ok: false, bills: [] };
  let res;
  try {
    res = await getEstimates({ bunitId, recordId: "", status: "PE", startRow: "0", endRow: "100", date: dayjs(date).format("YYYY-MM-DD"), documentno: "" });
  } catch {
    res = null;
  }
  if (res?.status !== 200) return { ok: false, bills: [] };
  const data = parseJson(res?.data?.data?.getEstimates, {});
  const bills = (data?.estimates || [])
    .filter((row) => row?.status === "PE" && row?.is_advanceorder !== "Y")
    .map((row) => ({ row, cart: parseJson(row.estimate_json, null) }))
    .filter(({ cart }) => cart && cart.floorSale === FLOOR_SALE && (cart.items || []).length > 0)
    .map(({ row, cart }) => ({
      estimateId: row.s_estimate_id,
      documentno: row.documentno || "",
      created: cart.floorSentAt || row.created || row.estimate_date || "",
      estimateDate: row.estimate_date || cart.floorSentAt || "",
      openedBy: cart.floorOpenedBy || null,
      cart,
    }))
    .sort((a, b) => String(b.created).localeCompare(String(a.created)));
  return { ok: true, bills };
}

const tillOf = (tillData) => tillData?.tillAccess?.cwrTill || {};
export const openedElsewhere = (bill, tillData) => !!bill?.openedBy?.tillId && bill.openedBy.tillId !== tillOf(tillData).cwrTillID;

// Before a till opens a floor bill it marks the estimate "opened on this till".
// The server refuses that write once the estimate is billed (CO) or cancelled
// (CL), so a stale list can't bill it twice; reading the mark back catches two
// tills opening it at the same moment (the later write wins, the other stops).
// → { ok: true, bill } or { ok: false, gone?, message }. Never throws.
export async function claimFloorBill(bill, tillData, { upsertEstimate, getEstimates }, now = dayjs()) {
  const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
  const till = tillOf(tillData);
  if (!bunitId || !bill?.estimateId) return { ok: false, message: "Couldn't open the bill. Try Refresh." };
  const openedBy = { tillId: till.cwrTillID || "", till: till.till || "", at: dayjs(now).format("YYYY-MM-DD HH:mm:ss") };
  const sentAt = dayjs(bill.estimateDate);
  let res;
  try {
    res = await upsertEstimate({
      bunitId,
      estimateId: bill.estimateId,
      date: (sentAt.isValid() ? sentAt : dayjs(now)).format("YYYY-MM-DD HH:mm:ss"),
      status: "PE",
      isAdvanceOrder: "N",
      estimateJson: { ...bill.cart, floorOpenedBy: openedBy },
    });
  } catch {
    res = null;
  }
  const out = res?.data?.data?.upsertEstimate;
  if (String(out?.status) === "201" && /completed|cancelled/i.test(out?.message || "")) {
    return { ok: false, gone: true, message: "This bill was already billed or cancelled on another till." };
  }
  if (String(out?.status) !== "200") return { ok: false, message: "Couldn't open the bill. Check the network and try again." };
  let check;
  try {
    check = await getEstimates({ bunitId, recordId: bill.estimateId, status: "PE", startRow: "0", endRow: "1", date: "", documentno: "" });
  } catch {
    check = null;
  }
  if (check?.status !== 200) return { ok: false, message: "Couldn't open the bill. Check the network and try again." };
  const row = (parseJson(check?.data?.data?.getEstimates, {})?.estimates || [])[0];
  const cart = row && parseJson(row.estimate_json, null);
  if (!cart) return { ok: false, gone: true, message: "This bill was already billed or cancelled on another till." };
  if (cart.floorOpenedBy?.tillId !== openedBy.tillId || cart.floorOpenedBy?.at !== openedBy.at) {
    return { ok: false, message: `${cart.floorOpenedBy?.till || "Another till"} opened this bill just now.` };
  }
  return { ok: true, bill: { ...bill, openedBy: cart.floorOpenedBy, cart } };
}

export function cartFromFloorBill(bill, { tillData, documentSequence, tillName }) {
  const cart = { ...bill.cart };
  // Payments are never carried over: the till takes them under its own sOrderID.
  cart.payments = [];
  cart.paid = 0;
  cart.estimateId = bill.estimateId;
  cart.tillName = tillName;
  // BUG-2548: bill under a new id; the bill links to the estimate by estimateId.
  cart.sOrderID = newId();
  cart.documentno = `${tillData?.tillAccess?.cwrTill?.prefix || ""}${Number(documentSequence || 0) + 1}`;
  // floorSale stays on, so the bill is listed again if the till parks it back.
  return cart;
}

export const bagIsEmpty = (cart) => !((cart?.items || []).length > 0 || (cart?.payments || []).length > 0);
